import { RackItem, RackVariant } from './merchandisingTypes';

export interface ProductPlanningOverride {
  sleeve?: 'short' | 'long' | 'sleeveless';
  thickness?: 'light' | 'medium' | 'heavy';
  season_kind?: 'summer' | 'transitional' | 'winter' | 'all_season';
  unit_cost_twd?: number | null;
}
export interface PlanningConfig {
  lead_days: number;
  selling_days: number;
  launch_piece_limit: number;
  launch_style_limit: number;
  launch_per_style_limit: number;
  purchase_budget_twd: number | null;
  season_mode: 'auto' | 'early_autumn' | 'autumn_transition';
  autumn_target_percent?: number;
  autumn_piece_limit?: number;
  autumn_style_limit?: number;
  rotation_style_limit?: number;
  preferred_skus: string[];
  product_overrides: Record<string, ProductPlanningOverride>;
}
export interface PlanningState { schema_ready: boolean; version: number; planning: PlanningConfig; editable: boolean }
export interface AutumnMix {
  fall_pieces: number; summer_pieces: number; unknown_pieces: number; winter_pieces: number; total_pieces: number; fall_percent: number; styles: number;
  tops?: { fall_pieces: number; total_pieces: number; fall_percent: number; styles: number };
}
export interface AutumnTransition {
  enabled: boolean; target_percent: number; current: AutumnMix; proposed: AutumnMix; added_styles: number; kept_styles: number; removed_styles: number;
  shortfall_pieces: number; tops_shortfall_pieces?: number; added_skus?: string[]; kept_skus?: string[]; removed_skus?: string[]; notes: string[];
}
export interface PlanningHorizon {
  lead_days: number; selling_days: number; arrival_start: string; arrival_end: string; selling_end: string;
  weather_covered_dates: string[]; weather_note: string; season_note: string;
}
export interface ProposedWorkRow {
  sku: string; title: string; image_url: string | null; variant_id: number | null; color: string | null; size: string | null;
  qty: number; target: number; on_hand: number; office: number; transit: number;
  rack_id: string; fixture_label: string; position: number; assortment_role: string; source_policy: string; reason: string;
  source_tag?: string | null; keep_qty?: number; requires_confirmation?: boolean; requires_adoption: boolean;
}
export interface ProposedWorklist {
  display: ProposedWorkRow[]; warehouse: ProposedWorkRow[]; order: ProposedWorkRow[];
  return: ProposedWorkRow[]; waiting: ProposedWorkRow[]; blocked: ProposedWorkRow[];
}
export interface LaunchCandidate extends RackItem {
  rank: number; selected: boolean; availability: 'ready' | 'blocked'; selection_reason: string;
  recommended_rack_id: string | null; launch_evidence?: unknown;
}
export interface LaunchReview {
  sku: string; title: string; action: string; reason: string; metrics?: RackItem['metrics']; requires_confirmation: boolean;
  review_status?: 'unknown' | 'observe' | 'replenish' | 'return';
  variants: (Pick<RackVariant, 'variant_id' | 'color' | 'size' | 'on_hand' | 'target'> & { sold_since_launch?: number | null })[];
}
export const validPlanningSku = (sku: string) => /^[A-Za-z0-9_-]{1,100}$/.test(sku);
export const normalizePlanning = (config: PlanningConfig): PlanningConfig => ({ autumn_target_percent: 70, autumn_piece_limit: 24, autumn_style_limit: 12, rotation_style_limit: 6, ...config });
export function validPlanning(config: PlanningConfig): boolean {
  const bounded = (value: number, low: number, high: number) => Number.isSafeInteger(value) && value >= low && value <= high;
  return bounded(config.lead_days, 1, 30) && bounded(config.selling_days, 3, 60)
    && bounded(config.launch_piece_limit, 0, 50) && bounded(config.launch_style_limit, 0, 20) && bounded(config.launch_per_style_limit, 1, 3)
    && ['auto', 'early_autumn', 'autumn_transition'].includes(config.season_mode)
    && bounded(config.autumn_target_percent ?? 70, 0, 100) && bounded(config.autumn_piece_limit ?? 24, 0, 100)
    && bounded(config.autumn_style_limit ?? 12, 0, 30) && bounded(config.rotation_style_limit ?? 6, 0, 20)
    && (config.purchase_budget_twd === null || bounded(config.purchase_budget_twd, 0, 1000000))
    && config.preferred_skus.length <= 100 && config.preferred_skus.every(validPlanningSku)
    && Object.keys(config.product_overrides).length <= 100 && Object.entries(config.product_overrides).every(([sku, value]) => validPlanningSku(sku)
      && (value.unit_cost_twd == null || bounded(value.unit_cost_twd, 0, Number.MAX_SAFE_INTEGER)));
}
